import AdminProTable from '@/components/admin-pro-table';
import { Button, Flex, Form } from 'antd';
import { FormattedMessage } from 'react-intl';

import { renderExpandedLogs } from '@/pages/ugc-reports/components/ReportLogsExpand';
import { useUgcReportList } from '@/pages/ugc-reports/hooks/useUgcReportList';
import { createDefaultSearchFormValues } from '@/pages/ugc-reports/utils';

import type { ProColumns } from '@ant-design/pro-components';
import type { ReportReasonDictionary, UgcReportSearchFormValues } from '@/pages/ugc-reports/utils';
import type { UgcReportTargetType, UgcReportTicket } from '@/services/ugc-reports';
import type { ReactNode } from 'react';

type Props = {
    targetType: UgcReportTargetType;
    buildColumns: (ctx: {
        expandedRowKeys: string[];
        toggleExpand: (record: UgcReportTicket) => void;
    }) => ProColumns<UgcReportTicket>[];
    searchFields: ReactNode;
    dictionary?: ReportReasonDictionary;
    scrollX?: number;
    withTimeFilter?: boolean;
};

const UgcReportListPage: React.FC<Props> = ({
    targetType,
    buildColumns,
    searchFields,
    dictionary = 'content',
    scrollX = 1600,
    withTimeFilter = true,
}) => {
    const initialValues: UgcReportSearchFormValues = createDefaultSearchFormValues(withTimeFilter);

    const [searchForm] = Form.useForm<UgcReportSearchFormValues>();
    const {
        dataSource,
        loading,
        pagination,
        expandedRowKeys,
        fetchList,
        handleSearch,
        handleResetSearch,
        handleExpand,
        formInitialValues,
    } = useUgcReportList(targetType, searchForm, { initialValues, withTimeFilter });

    const toggleExpand = (record: UgcReportTicket) => {
        const expanded = expandedRowKeys.includes(record.id);
        handleExpand(!expanded, record);
    };

    const columns = buildColumns({ expandedRowKeys, toggleExpand });

    return (
        <>
            <Form
                form={searchForm}
                initialValues={formInitialValues}
                onFinish={handleSearch}
                style={{ marginBottom: 16 }}
            >
                <Flex gap={16} wrap>
                    {searchFields}
                    <Button type="primary" htmlType="submit" loading={loading}>
                        <FormattedMessage id="b.search" />
                    </Button>
                    <Button onClick={handleResetSearch}>
                        <FormattedMessage id="b.reset" />
                    </Button>
                </Flex>
            </Form>
            <AdminProTable<UgcReportTicket>
                bordered
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                rowKey={(record) => record.id}
                search={false}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    onChange: fetchList,
                }}
                expandable={{
                    expandIcon: () => null,
                    expandedRowKeys,
                    onExpand: handleExpand,
                    expandedRowRender: (record) => renderExpandedLogs(record, dictionary),
                }}
                options={{
                    reload: () =>
                        fetchList(pagination.current as number, pagination.pageSize as number),
                    density: true,
                    setting: true,
                }}
                scroll={{ x: scrollX }}
            />
        </>
    );
};

export default UgcReportListPage;
